import { UseMutationResult, UseQueryResult } from '@tanstack/react-query';

import { getRequest as get } from 'api/utils/HttpMethods';

import { Payor, isPayor } from 'interfaces';
import {
  createEntity,
  updateEntity,
  useListQuery,
  useMutationQuery,
} from '../utils/queries';

export const payorQueryKeys = {
  all: ['payors'],
};

export const createPayor = async (payor: Payor): Promise<Payor> => {
  return createEntity<Payor>({
    entity: payor,
    path: '/resource/payor',
    functionName: 'createPayor',
    isEntity: isPayor,
  });
};

export const updatePayor = async (payor: Payor): Promise<Payor> => {
  return updateEntity<Payor>({
    entity: payor,
    path: '/resource/payor',
    functionName: 'updatePayor',
    isEntity: isPayor,
  });
};

/**
 * @description Fetches the list of payors from the server.
 * @returns {Promise<Payor[]>} A promise that resolves to an array of payors.
 */
const listPayors = async (): Promise<Payor[]> =>
  await get<Payor[]>('QBilling', `/resource/payor/all`);

/**
 * @description Hook to fetch the list of payors from the server.
 * @returns {UseQueryResult<Payor[], Error>} The result of the query.
 */
export const useListPayors = (): UseQueryResult<Payor[], Error> => {
  return useListQuery<Payor>({
    queryKey: payorQueryKeys.all,
    fetchFunction: listPayors,
    functionName: 'listPayors',
    isEntity: isPayor,
    enabled: true,
    displayNotification: true,
    staleTime: 1000 * 60 * 30,
    gcTime: 1000 * 60 * 60,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
};

export const useCreatePayor = (): UseMutationResult<Payor, Error> => {
  return useMutationQuery<Payor>({
    mutationFn: createPayor,
    successMessage: 'Payor Created',
    errorMessage: 'Failed to create payor',
    queryKey: payorQueryKeys.all,
  });
};

export const useUpdatePayor = (): UseMutationResult<Payor, Error> => {
  return useMutationQuery<Payor>({
    mutationFn: updatePayor,
    successMessage: 'Payor Updated',
    errorMessage: 'Failed to update payor',
    queryKey: payorQueryKeys.all,
  });
};
